// Copyright (c) Said Borna. All rights reserved.

/** User roles in the BAM system (global). */
export type UserRole = 'admin' | 'staff' | 'client';

/** Per-team staff role (stored in team_staff_assignments). */
export type TeamRole = 'writer' | 'lead_writer';

/** Post lifecycle statuses. */
export type PostStatus =
    | 'draft'
    | 'pending'
    | 'approved'
    | 'published'
    | 'declined'
    | 'cancelled'
    | 'expired'
    | 'failed';

/** Invitation statuses. */
export type InvitationStatus = 'pending' | 'accepted' | 'expired' | 'revoked';

/** Post action types from email tokens. */
export type PostActionType = 'approve' | 'decline';

/** AI conversation statuses. */
export type ConversationStatus = 'active' | 'archived';

/** Post validation statuses. */
export type ValidationStatus = 'pending' | 'passed' | 'failed' | 'skipped';

/** LinkedIn visibility options. */
export type LinkedInVisibility = 'PUBLIC' | 'CONNECTIONS' | 'LOGGED_IN';

/** Notification scope for team members. */
export type NotificationScope = 'none' | 'own' | 'all';

/** Approval/decline channel. */
export type ActionChannel = 'app' | 'email';

/** Profile status. */
export type ProfileStatus = 'active' | 'inactive' | 'pending_activation';

/** Inspiration source types. */
export type InspirationSourceType = 'linkedin_profile' | 'news_feed';

/**
 * User profile — extends Supabase auth.users.
 */
export interface Profile {
    readonly id: string;
    readonly email: string;
    readonly full_name: string | null;
    readonly status: ProfileStatus;
    readonly publish_paused?: boolean;
    readonly activated_at: string | null;
    readonly activated_by: string | null;
    readonly created_at: string;
}

/**
 * User role assignment.
 */
export interface UserRoleRecord {
    readonly id: string;
    readonly user_id: string;
    readonly role: UserRole;
    readonly created_at: string;
    readonly created_by: string | null;
}

/**
 * Team — billable entity.
 */
export interface Team {
    readonly id: string;
    readonly name: string;
    readonly slack_webhook_url: string | null;
    readonly slack_channel_name: string | null;
    readonly created_at: string;
    readonly created_by: string | null;
}

/**
 * Team member — links profiles to teams.
 */
export interface TeamMember {
    readonly id: string;
    readonly team_id: string;
    readonly user_id: string;
    readonly post_quota_weekly: number | null;
    readonly notification_scope: NotificationScope;
    readonly timezone: string;
    readonly added_at: string;
    readonly added_by: string | null;
}

/**
 * Team staff assignment — multiple staff per team with per-team roles.
 */
export interface TeamStaffAssignment {
    readonly team_id: string;
    readonly staff_id: string | null;
    readonly team_role: TeamRole;
    readonly assigned_at: string;
}

/**
 * Engagement data shape for imported posts.
 */
export interface EngagementData {
    readonly likes?: number;
    readonly comments?: number;
    readonly shares?: number;
    readonly impressions?: number;
}

/**
 * Validation result from AI quality check.
 */
export interface ValidationResult {
    readonly passed: boolean;
    readonly score: number;
    readonly issues: ReadonlyArray<{
        readonly rule: string;
        readonly severity: 'error' | 'warning' | 'info';
        readonly suggestion: string;
    }>;
}

/**
 * Post — central entity in the system.
 */
export interface Post {
    readonly id: string;
    readonly user_id: string;
    readonly content: string;
    readonly status: PostStatus;
    readonly scheduled_date: string | null;
    readonly published_date: string | null;
    readonly pipeline_position: number | null;
    readonly created_by: string | null;
    readonly handled_by: string | null;
    readonly published_by: string | null;
    readonly user_feedback: string | null;
    readonly approved_via: ActionChannel | null;
    readonly declined_via: ActionChannel | null;
    readonly linkedin_enabled: boolean;
    readonly linkedin_visibility: LinkedInVisibility;
    readonly linkedin_post_id: string | null;
    readonly linkedin_post_url: string | null;
    readonly linkedin_published_at: string | null;
    readonly linkedin_error_message: string | null;
    readonly linkedin_publish_attempts: number;
    readonly linkedin_last_attempt_at: string | null;
    readonly requires_manual_publishing: boolean;
    readonly article_url: string | null;
    readonly article_title: string | null;
    readonly article_description: string | null;
    readonly article_thumbnail_url: string | null;
    readonly content_pillars: readonly string[];
    readonly notification_sent_at: string | null;
    readonly notification_sent_by: string | null;
    readonly notification_count: number;
    readonly validation_status: ValidationStatus | null;
    readonly validation_result: ValidationResult | null;
    readonly validated_at: string | null;
    readonly imported_from: string | null;
    readonly original_engagement: EngagementData | null;
    readonly needs_review: boolean;
    readonly reviewed_by: string | null;
    readonly reviewed_at: string | null;
    readonly created_at: string;
    readonly updated_at: string;
}

/**
 * Publishing slot — recurring weekly schedule entry.
 */
export interface PublishingSlot {
    readonly id: string;
    readonly user_id: string;
    readonly day_of_week: number;
    readonly time_of_day: string;
    readonly timezone: string;
    readonly created_by: string | null;
    readonly created_at: string;
}

/**
 * Customer Super Prompt — per-client AI voice configuration.
 */
export interface CustomerSuperPrompt {
    readonly id: string;
    readonly user_id: string;
    readonly prompt_data: SuperPromptData;
    readonly assembled_prompt: string | null;
    readonly template_version: string;
    readonly prompt_version: number;
    readonly is_auto_generated: boolean;
    readonly generated_from_profile_id: string | null;
    readonly created_by: string | null;
    readonly last_modified_by: string | null;
    readonly created_at: string;
    readonly updated_at: string;
}

/**
 * Super Prompt structured data.
 */
export interface SuperPromptData {
    readonly customer: {
        readonly firstName: string;
        readonly lastName: string;
        readonly role: string;
        readonly company: string;
        readonly primaryAudience: string;
        readonly overarchingGoal: string;
        readonly companyInfo: {
            readonly websiteScrape: string;
            readonly coreOffering: string;
            readonly additionalDetails: string;
        };
        readonly professionalKeyPoints: string;
        readonly personalInformation: string;
        readonly definitionOfSuccess: string;
        readonly previousPosts: readonly string[];
        readonly contentPillars: readonly string[];
    };
    readonly preferences: {
        readonly toneAndStyle: string;
        readonly brandVoiceNotes: string;
        readonly toneFeedback: string;
    };
    readonly formattingRules: string;
}

/**
 * AI Write Brain — global AI rules.
 */
export interface AIWriteBrain {
    readonly id: string;
    readonly rules: string;
    readonly version: number;
    readonly is_active: boolean;
    readonly created_by: string | null;
    readonly updated_at: string;
}

/**
 * Inspiration source — LinkedIn profile or news feed.
 */
export interface InspirationSource {
    readonly id: string;
    readonly source_type: InspirationSourceType;
    readonly name: string;
    readonly url: string;
    readonly linkedin_person_id: string | null;
    readonly added_by: string | null;
    readonly team_id: string | null;
    readonly created_at: string;
}

/**
 * Content idea submitted by a client.
 */
export interface ContentIdea {
    readonly id: string;
    readonly user_id: string;
    readonly title: string;
    readonly description: string | null;
    readonly is_reviewed: boolean;
    readonly created_at: string;
}

/**
 * Inspiration post — fetched from an inspiration source.
 */
export interface InspirationPost {
    readonly id: string;
    readonly source_id: string;
    readonly content: string;
    readonly external_url: string | null;
    readonly engagement: EngagementData | null;
    readonly published_at: string | null;
    readonly fetched_at: string;
}

/**
 * Post history import — historical LinkedIn post for tone analysis.
 */
export interface PostHistoryImport {
    readonly id: string;
    readonly user_id: string;
    readonly content: string;
    readonly engagement: EngagementData | null;
    readonly original_published_at: string | null;
    readonly imported_at: string;
    readonly imported_by: string | null;
}

/**
 * Slack notification event types.
 */
export type SlackEventType = 'post_published' | 'post_approved' | 'post_declined' | 'linkedin_error';

/**
 * Invitation record for user signup.
 */
export interface Invitation {
    readonly id: string;
    readonly email: string;
    readonly role: UserRole;
    readonly team_id: string | null;
    readonly token: string;
    readonly status: InvitationStatus;
    readonly invited_by: string;
    readonly expires_at: string;
    readonly created_at: string;
}

/**
 * AI conversation session.
 */
export interface AIConversation {
    readonly id: string;
    readonly user_id: string;
    readonly title: string | null;
    readonly status: ConversationStatus;
    readonly client_id: string | null;
    readonly total_tokens: number;
    readonly total_cost: number;
    readonly created_at: string;
    readonly updated_at: string;
}

/** AI message role. */
export type AIMessageRole = 'user' | 'assistant';

/**
 * AI message within a conversation.
 */
export interface AIMessage {
    readonly id: string;
    readonly conversation_id: string;
    readonly role: AIMessageRole;
    readonly content: string;
    readonly tool_calls: ReadonlyArray<AIToolCall> | null;
    readonly input_tokens: number;
    readonly output_tokens: number;
    readonly cost: number;
    readonly created_at: string;
}

/**
 * AI tool call made during a message.
 */
export interface AIToolCall {
    readonly tool_name: string;
    readonly input: Record<string, unknown>;
    readonly output: unknown;
    readonly duration_ms: number;
}

/**
 * AI post proposal — generated draft pending review.
 */
export interface AIPostProposal {
    readonly id: string;
    readonly conversation_id: string;
    readonly client_id: string;
    readonly content: string;
    readonly content_pillars: readonly string[];
    readonly status: 'draft' | 'accepted' | 'rejected';
    readonly accepted_post_id: string | null;
    readonly created_at: string;
}

/**
 * AI chat request payload.
 */
export interface AIChatRequest {
    readonly conversationId: string;
    readonly message: string;
    readonly clientId?: string;
    readonly temperature?: number;
}

/**
 * AI chat response from edge function.
 */
export interface AIChatResponse {
    readonly message: AIMessage;
    readonly conversationId: string;
}

/**
 * Authenticated user context — profile + role combined.
 */
export interface AuthUser {
    readonly id: string;
    readonly email: string;
    readonly fullName: string | null;
    readonly role: UserRole;
    readonly status: ProfileStatus;
}

/** LinkedIn OAuth token status. */
export type LinkedInTokenStatus = 'active' | 'expired' | 'revoked' | 'error';

/**
 * LinkedIn OAuth token record.
 */
export interface LinkedInOAuthToken {
    readonly id: string;
    readonly user_id: string;
    readonly access_token: string;
    readonly refresh_token: string | null;
    readonly linkedin_person_id: string | null;
    readonly expires_at: string;
    readonly scopes: readonly string[];
    readonly status: LinkedInTokenStatus;
    readonly error_message: string | null;
    readonly created_at: string;
    readonly updated_at: string;
}

/**
 * LinkedIn connection status for display purposes (no token exposed).
 */
export interface LinkedInConnectionStatus {
    readonly isConnected: boolean;
    readonly status: LinkedInTokenStatus | null;
    readonly linkedinPersonId: string | null;
    readonly expiresAt: string | null;
    readonly errorMessage: string | null;
}

/**
 * Pipeline post — post with position for drag-and-drop reordering.
 */
export interface PipelinePost {
    readonly id: string;
    readonly user_id: string;
    readonly content: string;
    readonly pipeline_position: number;
    readonly scheduled_date: string | null;
    readonly created_at: string;
    readonly updated_at: string;
    readonly profile: Pick<Profile, 'id' | 'full_name' | 'email'> | null;
}

/**
 * Calendar entry — represents a post or slot occurrence on a date.
 */
export interface CalendarEntry {
    readonly id: string;
    readonly date: string;
    readonly type: 'published' | 'scheduled' | 'slot' | 'pending';
    readonly title: string;
    readonly postId?: string;
    readonly slotId?: string;
    readonly status?: PostStatus;
    /** Time of day in HH:MM format (from slot or scheduled time). */
    readonly time?: string;
    /** IANA timezone for the time (from publishing slot or client). */
    readonly timezone?: string;
}

// ---------------------------------------------------------------------------
// Phase 7: Ops & Polish types
// ---------------------------------------------------------------------------

/** Media file types supported by the platform. */
export type MediaType = 'image' | 'video' | 'document';

/**
 * User-uploaded media file.
 */
export interface UserMedia {
    readonly id: string;
    readonly user_id: string;
    readonly file_name: string;
    readonly file_type: MediaType;
    readonly mime_type: string;
    readonly file_size: number;
    readonly storage_path: string;
    readonly public_url: string | null;
    readonly alt_text: string | null;
    readonly created_at: string;
}

/**
 * Media attachment linked to a content idea.
 */
export interface ContentIdeaMedia {
    readonly id: string;
    readonly idea_id: string;
    readonly media_url: string;
    readonly media_type: MediaType;
    readonly file_name: string | null;
    readonly created_at: string;
}

/**
 * Content idea with optional media attachments.
 */
export interface ContentIdeaWithMedia extends ContentIdea {
    readonly media: ReadonlyArray<ContentIdeaMedia>;
}

/** Team activity event types for ops dashboard. */
export type ActivityEventType =
    | 'post_created'
    | 'post_declined'
    | 'post_approved'
    | 'post_published'
    | 'idea_submitted'
    | 'feedback_given';

/**
 * Team activity event for ops dashboard.
 */
export interface TeamActivity {
    readonly id: string;
    readonly team_id: string | null;
    readonly user_id: string;
    readonly action: ActivityEventType;
    readonly target_type: string;
    readonly target_id: string;
    readonly metadata: Record<string, unknown>;
    readonly created_at: string;
}

/**
 * Ops dashboard summary metrics.
 */
export interface OpsDashboardMetrics {
    readonly deliveryCoverage: ReadonlyArray<DeliveryCoverageEntry>;
    readonly actionItems: ReadonlyArray<ActionItem>;
    readonly recentActivity: ReadonlyArray<TeamActivity>;
}

/**
 * Delivery coverage per client: do they have upcoming posts?
 */
export interface DeliveryCoverageEntry {
    readonly userId: string;
    readonly userName: string;
    readonly teamName: string;
    readonly upcomingPosts: number;
    readonly lastPublished: string | null;
    readonly hasGap: boolean;
}

/**
 * Actionable item surfaced in ops dashboard.
 */
export interface ActionItem {
    readonly type: 'content_gap' | 'pending_review' | 'failed_publish' | 'unreviewed_idea';
    readonly severity: 'high' | 'medium' | 'low';
    readonly title: string;
    readonly description: string;
    readonly targetId: string | null;
    readonly targetPath: string | null;
}

/**
 * Check if a role is a staff role (admin or staff).
 */
export function isStaffRole(role: UserRole): boolean {
    return role === 'admin' || role === 'staff';
}

/**
 * Check if user can promote drafts to pending.
 * Admin always can. Staff needs lead_writer team_role.
 */
export function canPromoteToPending(role: UserRole, teamRole?: TeamRole): boolean {
    return role === 'admin' || teamRole === 'lead_writer';
}

/**
 * Get display name for a profile (full_name or email fallback).
 */
export function getDisplayName(profile: Pick<Profile, 'full_name' | 'email'>): string {
    return profile.full_name ?? profile.email;
}

// ---------------------------------------------------------------------------
// Phase 9: Data Migration & LinkedIn Polish types
// ---------------------------------------------------------------------------

/**
 * Structured tone of voice profile generated by AI analysis.
 */
export interface VoiceProfileData {
    readonly writingStyle: string;
    readonly toneDescriptors: readonly string[];
    readonly vocabularyLevel: string;
    readonly sentenceStructure: string;
    readonly hookPatterns: readonly string[];
    readonly ctaPatterns: readonly string[];
    readonly emojiUsage: string;
    readonly hashtagStyle: string;
    readonly topicThemes: readonly string[];
    readonly audienceApproach: string;
    readonly uniqueTraits: readonly string[];
}

/**
 * Versioned voice profile record from AI generation.
 */
export interface VoiceProfileVersion {
    readonly id: string;
    readonly user_id: string;
    readonly version: number;
    readonly profile_data: VoiceProfileData;
    readonly source_post_count: number;
    readonly generated_by: string | null;
    readonly created_at: string;
}

/**
 * LinkedIn organization record for company mentions.
 */
export interface LinkedInOrganization {
    readonly id: string;
    readonly name: string;
    readonly vanity_name: string | null;
    readonly organization_urn: string;
    readonly logo_url: string | null;
    readonly created_at: string;
}

/**
 * Link metadata extracted from OG tags for article previews.
 */
export interface LinkMetadata {
    readonly url: string;
    readonly title: string | null;
    readonly description: string | null;
    readonly image: string | null;
    readonly siteName: string | null;
}

/**
 * Resolved LinkedIn company info from URL/vanity name scraping.
 */
export interface ResolvedCompany {
    readonly name: string;
    readonly orgId: string;
    readonly vanityName: string;
}

/**
 * Post-to-company mention junction record.
 */
export interface PostCompanyMention {
    readonly id: string;
    readonly post_id: string;
    readonly organization_id: string;
    readonly created_at: string;
}

// ── LinkedIn Media Types ────────────────────────────────────────────────────

/** Media type category for LinkedIn uploads. */
export type LinkedInMediaType = 'image' | 'video' | 'document';

/** Result of a LinkedIn media upload operation. */
export interface LinkedInUploadResult {
    readonly success: boolean;
    readonly imageUrn?: string;
    readonly documentUrn?: string;
    readonly error?: string;
    readonly errorCode?: string;
    readonly isRetryable?: boolean;
}

/** Post media attachment record (post ↔ user_media junction). */
export interface PostMediaAttachment {
    readonly id: string;
    readonly post_id: string;
    readonly media_id: string;
    readonly position: number;
    readonly created_at: string;
}

/** Post media attachment with resolved media details. */
export interface PostMediaAttachmentWithMedia extends PostMediaAttachment {
    readonly user_media: {
        readonly id: string;
        readonly storage_path: string;
        readonly file_name: string;
        readonly file_type: string;
        readonly file_size: number | null;
        readonly alt_text: string | null;
    };
}

/** Result of creating a LinkedIn post via modern Posts API. */
export interface LinkedInPostResult {
    readonly success: boolean;
    readonly postId?: string;
    readonly postUrl?: string;
    readonly error?: string;
}

/**
 * LinkedIn post fetched from API during import.
 */
export interface LinkedInFetchedPost {
    readonly content: string;
    readonly publishedAt: string | null;
    readonly engagement: EngagementData;
    readonly linkedinPostId: string | null;
}

/**
 * Import progress status for LinkedIn API fetch.
 */
export interface ImportProgress {
    readonly total: number;
    readonly imported: number;
    readonly failed: number;
    readonly status: 'idle' | 'fetching' | 'importing' | 'analyzing' | 'complete' | 'error';
    readonly errorMessage: string | null;
}

/**
 * BAM 1.0 migration verification report.
 */
export interface MigrationReport {
    readonly tableName: string;
    readonly sourceCount: number;
    readonly importedCount: number;
    readonly skippedCount: number;
    readonly errors: readonly string[];
}

// ---------------------------------------------------------------------------
// Block 1: Memory System types
// ---------------------------------------------------------------------------

/** Memory classification types. */
export type MemoryType = 'preference' | 'style' | 'topic' | 'formatting' | 'tone' | 'avoid';

/** Source of how a memory was generated. */
export type MemorySourceType = 'feedback' | 'edit_diff' | 'manual' | 'ai_analysis';

/**
 * Client memory — auto-generated from feedback/edits, used in prompt assembly.
 */
export interface ClientMemory {
    readonly id: string;
    readonly user_id: string;
    readonly memory_text: string;
    readonly memory_type: MemoryType;
    readonly source_type: MemorySourceType;
    readonly source_post_id: string | null;
    readonly confidence: number;
    readonly is_active: boolean;
    readonly reinforcement_count: number;
    readonly created_by: string | null;
    readonly deactivated_at: string | null;
    readonly created_at: string;
    readonly updated_at: string;
}

/**
 * Response from the ai-generate-memory Edge Function.
 */
export interface GenerateMemoryResult {
    readonly memories: ReadonlyArray<ClientMemory>;
    readonly processedCount: number;
}

// ---------------------------------------------------------------------------
// Block 2: Client Editing Flow types
// ---------------------------------------------------------------------------

/** Revision source type. */
export type RevisionType = 'client_edit' | 'writer_edit';

/**
 * Post revision — tracks original vs edited content.
 */
export interface PostRevision {
    readonly id: string;
    readonly post_id: string;
    readonly original_content: string;
    readonly edited_content: string;
    readonly edited_by: string | null;
    readonly revision_type: RevisionType;
    readonly created_at: string;
}

// ---------------------------------------------------------------------------
// Block 7: Tone Corpus types
// ---------------------------------------------------------------------------

/** Source type for tone corpus documents. */
export type CorpusSourceType = 'post' | 'email' | 'chat' | 'document';

/**
 * Tone corpus document — any text source used to build a client's voice profile.
 */
export interface ToneCorpusDocument {
    readonly id: string;
    readonly user_id: string;
    readonly title: string;
    readonly source_type: CorpusSourceType;
    readonly content: string;
    readonly word_count: number;
    readonly file_name: string | null;
    readonly corpus_version: number;
    readonly created_by: string | null;
    readonly created_at: string;
}

/**
 * Structured tone profile — AI-analyzed voice characteristics.
 * Matches the 8-card layout from BAM Prompter.
 */
export interface ToneProfileData {
    readonly voiceAdjectives: readonly string[];
    readonly defaultPointOfView: string;
    readonly sentenceCadence: {
        readonly avgLength: number;
        readonly variation: string;
    };
    readonly emojiUsage: {
        readonly frequency: string;
        readonly preferred: readonly string[];
        readonly notes: string;
    };
    readonly formattingConventions: {
        readonly lineBreaks: string;
        readonly bullets: string;
        readonly hooks: readonly string[];
    };
    readonly ctaStyle: {
        readonly aggressiveness: string;
        readonly examples: readonly string[];
    };
    readonly signaturePhrases: readonly string[];
    readonly tabooPhrases: readonly string[];
}

/**
 * Versioned tone profile record.
 */
export interface ToneProfile {
    readonly id: string;
    readonly user_id: string;
    readonly version: number;
    readonly profile_data: ToneProfileData;
    readonly source_document_count: number;
    readonly source_word_count: number;
    readonly generated_by: string | null;
    readonly created_at: string;
}

// ---------------------------------------------------------------------------
// Block 9: Post Edit Analysis types
// ---------------------------------------------------------------------------

/** A single granular diff change item. */
export interface DiffChangeItem {
    readonly id: string;
    readonly type: 'addition' | 'deletion' | 'modification';
    readonly original: string;
    readonly edited: string;
    readonly accepted: boolean | null;
}

/** Holistic AI analysis of an edit pair. */
export interface HolisticAnalysis {
    readonly summary: string;
    readonly primaryChanges: readonly string[];
    readonly toneShift: string;
    readonly structuralChanges: string;
    readonly ctaChange: string;
    readonly dimensions: readonly {
        readonly name: string;
        readonly direction: 'increased' | 'decreased' | 'unchanged';
        readonly note: string;
    }[];
}

/**
 * Post edit analysis — stores granular diff + holistic AI analysis.
 */
export interface PostEditAnalysis {
    readonly id: string;
    readonly post_id: string | null;
    readonly user_id: string;
    readonly original_content: string;
    readonly edited_content: string;
    readonly granular_diff: readonly DiffChangeItem[];
    readonly holistic_analysis: HolisticAnalysis | null;
    readonly accepted_changes: number;
    readonly rejected_changes: number;
    readonly total_changes: number;
    readonly final_content: string | null;
    readonly analyzed_by: string | null;
    readonly created_at: string;
    readonly updated_at: string;
}

// ---------------------------------------------------------------------------
// Block 10: Prompt Suggestion types
// ---------------------------------------------------------------------------

/** Status of a prompt suggestion. */
export type SuggestionStatus = 'pending' | 'accepted' | 'rejected' | 'deferred';

/** A single change item within a suggestion. */
export interface SuggestionChange {
    readonly number: number;
    readonly section: string;
    readonly description: string;
    readonly before: string;
    readonly after: string;
}

/** A risk note attached to a suggestion. */
export interface SuggestionRiskNote {
    readonly severity: 'low' | 'medium' | 'high';
    readonly message: string;
}

/** Diff preview for the suggestion modal. */
export interface SuggestionDiffPreview {
    readonly additions: readonly string[];
    readonly deletions: readonly string[];
}

/**
 * Prompt suggestion — AI-generated recommendation for updating a Super Prompt.
 */
export interface PromptSuggestion {
    readonly id: string;
    readonly user_id: string;
    readonly suggestion_number: number;
    readonly status: SuggestionStatus;
    readonly changes: readonly SuggestionChange[];
    readonly risk_notes: readonly SuggestionRiskNote[];
    readonly diff_preview: SuggestionDiffPreview | null;
    readonly reasoning: string | null;
    readonly generated_by: string | null;
    readonly resolved_by: string | null;
    readonly resolved_at: string | null;
    readonly created_at: string;
}

// ---------------------------------------------------------------------------
// Block 11: Edit Insights types
// ---------------------------------------------------------------------------

/** Time range filter for insights. */
export type InsightsTimeRange = '7d' | '30d' | '90d';

/** KPI summary for edit insights. */
export interface EditInsightsKPIs {
    readonly postsAnalyzed: number;
    readonly avgEditsPerPost: number;
    readonly zeroEditApprovalPct: number;
}

/** A single tag with frequency count from holistic dimensions. */
export interface EditTag {
    readonly name: string;
    readonly count: number;
}

/** An edit pattern derived from analyses. */
export interface EditPattern {
    readonly description: string;
    readonly frequency: number;
    readonly impact: 'high' | 'medium' | 'low';
    readonly examples: readonly string[];
}

/** Weekly edit volume data point. */
export interface EditVolumePoint {
    readonly week: string;
    readonly count: number;
}

/** Full edit insights payload. */
export interface EditInsightsData {
    readonly kpis: EditInsightsKPIs;
    readonly topTags: readonly EditTag[];
    readonly editPatterns: readonly EditPattern[];
    readonly editVolume: readonly EditVolumePoint[];
}

/** Prompt configuration for an Edge Function. */
export interface PromptConfig {
    readonly id: string;
    readonly function_key: string;
    readonly display_name: string;
    readonly description: string;
    readonly system_prompt: string;
    readonly is_active: boolean;
    readonly updated_by: string | null;
    readonly updated_at: string;
    readonly created_at: string;
}
